import { useState, useEffect } from 'react';
import { toast } from '@/hooks/use-toast';

export const usePushNotifications = () => {
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    if ('Notification' in window) {
      setIsSupported(true);
      setPermission(Notification.permission);
    }
  }, []);

  const requestPermission = async () => {
    if (!isSupported) {
      toast({
        title: "Not Supported",
        description: "Push notifications are not supported in this browser",
        variant: "destructive"
      });
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        toast({
          title: "Notifications Enabled!",
          description: "You'll receive updates about your orders and exclusive offers"
        });
        return true;
      } else if (result === 'denied') {
        toast({
          title: "Notifications Blocked",
          description: "You can enable them later in your browser settings",
          variant: "destructive"
        });
      }
      return false;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  };

  const sendLocalNotification = (title: string, options?: NotificationOptions) => {
    if (permission !== 'granted') {
      console.warn('Notification permission not granted');
      return;
    }

    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      // Send notification via service worker for better persistence
      navigator.serviceWorker.ready.then((registration) => {
        registration.showNotification(title, {
          icon: '/icon-192.png',
          badge: '/icon-192.png',
          ...options
        });
      });
    } else {
      // Fallback to regular notification
      new Notification(title, {
        icon: '/icon-192.png',
        ...options
      });
    }
  };

  const sendPurchaseThankYou = (productName: string, relatedProduct?: { name: string; id: string }) => {
    const body = relatedProduct
      ? `Thank you for purchasing ${productName}! Check out ${relatedProduct.name} - you might love it too!`
      : `Thank you for purchasing ${productName}! Your order is being processed.`;

    sendLocalNotification('Purchase Confirmed! 🎉', {
      body,
      tag: 'purchase-thankyou',
      requireInteraction: false,
      data: relatedProduct ? { type: 'product-suggestion', productId: relatedProduct.id } : undefined
    });
  };

  const sendOrderUpdate = (status: string, orderNumber: string) => {
    sendLocalNotification('Order Update', {
      body: `Your order #${orderNumber} is now ${status}`,
      tag: `order-${orderNumber}`,
      requireInteraction: true
    });
  };

  const sendSaleAlert = (message: string) => {
    sendLocalNotification('🔥 Exclusive Sale!', {
      body: message,
      tag: 'sale-alert',
      requireInteraction: true
    });
  };

  return {
    permission,
    isSupported,
    requestPermission,
    sendLocalNotification,
    sendPurchaseThankYou,
    sendOrderUpdate,
    sendSaleAlert
  };
};
